import { PaginatedDocketItems, isOverflowingDocket } from '../../layoutHelper';
import {
  DocketDisplayItem,
  DocketDisplayItemType,
  DocketDisplayOrderItem,
  DocketDisplayOrder,
} from '../../../common/types';
import {
  DocketDisplayItemTypeHeight,
  CONTINUE,
  headerOrder,
} from '../../../common/constants';
import { DocketStatus } from '@oolio-group/domain';

// Helper function to check if an item is substantial content
const isSubstantialContentItem = (item: DocketDisplayItem): boolean => {
  return ![
    DocketDisplayItemType.DOCKET_HEADER,
    DocketDisplayItemType.COURSE,
    DocketDisplayItemType.SEAT_NUMBER,
    DocketDisplayItemType.CATEGORY,
    DocketDisplayItemType.CONTINUE,
    DocketDisplayItemType.CONTINUED,
    DocketDisplayItemType.TRAINING_DOCKET,
    DocketDisplayItemType.DOCKET_TITLE_BAR,
    DocketDisplayItemType.RECALLED_TAG,
    DocketDisplayItemType.VOID,
    DocketDisplayItemType.ORDER_NOTE,
    DocketDisplayItemType.OTHER_ITEMS,
    DocketDisplayItemType.NOTIFIER_HEADER,
    DocketDisplayItemType.NOTIFIER_PROFILE,
    DocketDisplayItemType.RECALL_BUTTON,
  ].includes(item.type);
};

// Helper function to check if a column has substantial content
const columnHasSubstantialContent = (items: DocketDisplayItem[]): boolean => {
  return items.some(isSubstantialContentItem);
};

/**
 * Simple rule: No column breaks without substantial content
 * Evaluates whether to break to a new column based on overflow and substantial content
 * @param wouldOverflow - Whether normal overflow conditions are met
 * @param currentItem - The item being evaluated
 * @param currentItemIndex - Index of current item
 * @param currentColumnItems - Items already in current column
 * @returns true if should break to new column
 */
const shouldBreakToNewColumn = (
  wouldOverflow: boolean,
  currentItem: DocketDisplayItem,
  currentItemIndex: number,
  currentColumnItems: DocketDisplayItem[],
): boolean => {
  // Don't break if this isn't an overflow situation or we're at the first item
  if (!wouldOverflow || currentItemIndex === 0) {
    return false;
  }

  // Never break for OTHER_ITEMS - always keep them with previous content
  if (
    currentItem.type === DocketDisplayItemType.OTHER_ITEMS ||
    currentItem.type === DocketDisplayItemType.RECALL_BUTTON
  ) {
    return false;
  }

  // Simple rule: No column breaks without substantial content
  const hasSubstantialContent = columnHasSubstantialContent(currentColumnItems);

  return hasSubstantialContent;
};

const extractDocketProperty = (
  docketItems: DocketDisplayItem[],
  headerExtractor: (item: DocketDisplayOrderItem) => string | undefined,
  nonHeaderExtractor: (item: DocketDisplayOrderItem) => string | undefined,
): string => {
  const header = docketItems.find(
    item => item.type === DocketDisplayItemType.DOCKET_HEADER,
  );

  if (header) {
    const headerValue = headerExtractor(header as DocketDisplayOrderItem);
    if (headerValue) return headerValue;
  }

  const firstNonHeader = docketItems.find(
    item => item.type !== DocketDisplayItemType.DOCKET_HEADER,
  );

  if (firstNonHeader) {
    return nonHeaderExtractor(firstNonHeader as DocketDisplayOrderItem) || '';
  }

  return '';
};

const getDocketId = (docketItems: DocketDisplayItem[]): string => {
  return extractDocketProperty(
    docketItems,
    item => item.id || item.docketId,
    item => item.docketId,
  );
};

const getOrderNumber = (docketItems: DocketDisplayItem[]): string => {
  return extractDocketProperty(
    docketItems,
    item => item.orderNumber,
    item => item.orderNumber,
  );
};

const getDocketStatus = (docketItems: DocketDisplayItem[]): DocketStatus => {
  const header = docketItems.find(
    item => item.type === DocketDisplayItemType.DOCKET_HEADER,
  );

  if (header && (header as DocketDisplayOrder).status) {
    return (header as DocketDisplayOrder).status;
  }
  return DocketStatus.CREATED; // Default status if not found
};

const checkForOrphans = (
  docketItems: DocketDisplayItem[],
  scale: (n: number) => number,
  columnHeight: number,
  continueHeight: number,
): boolean => {
  let height = 0;
  const currentColumnItems: DocketDisplayItem[] = [];

  for (let i = 0; i < docketItems.length; i++) {
    const item = docketItems[i];
    const itemHeight = scale(item.height);
    const isLastItemForDocket = i === docketItems.length - 1;
    const heightNeeded = isLastItemForDocket
      ? itemHeight
      : itemHeight + continueHeight;

    // Check if we would overflow with this item
    const wouldOverflow =
      isOverflowingDocket(
        docketItems,
        i,
        columnHeight,
        height,
        continueHeight,
        scale,
        true,
      ) ||
      (height + heightNeeded > columnHeight && !isLastItemForDocket) ||
      height + itemHeight > columnHeight ||
      (i > 0 &&
        headerOrder.some((headerType, priorityIndex) => {
          if (item.type === headerType) {
            if (priorityIndex === 0) return true;
            const previousItemType = docketItems[i - 1]?.type;
            return !headerOrder
              .slice(0, priorityIndex)
              .includes(previousItemType);
          }
          return false;
        }));

    // Use the simplified substantial content logic
    const shouldBreak = shouldBreakToNewColumn(
      wouldOverflow,
      item,
      i,
      currentColumnItems,
    );

    if (shouldBreak) {
      return true;
    }

    currentColumnItems.push(item);
    height += itemHeight;
  }
  return false;
};

const createPage = (pageNumber: number, cols: number, rows: number) => {
  return {
    page: pageNumber,
    columns: Array.from({ length: cols }, (_, colIndex) => ({
      column: colIndex + 1,
      rows: Array.from({ length: rows }, (_, rowIndex) => ({
        row: rowIndex + 1,
        items: [] as DocketDisplayItem[],
      })),
    })),
  };
};

// Fixed calculateTotalPositions function that mirrors the exact main layout logic
const calculateTotalPositions = (
  docketItems: DocketDisplayItem[],
  scale: (n: number) => number,
  columnHeight: number,
  continueHeight: number,
  continuedHeight: number,
): number => {
  let totalPositions = 1;
  let simHeight = 0;
  let currentColumnItems: DocketDisplayItem[] = [];

  for (let itemIndex = 0; itemIndex < docketItems.length; itemIndex++) {
    const item = docketItems[itemIndex];
    const itemHeight = scale(item.height);
    const isLastItemForDocket = itemIndex === docketItems.length - 1;
    const heightNeeded = isLastItemForDocket
      ? itemHeight
      : itemHeight + continueHeight;

    const wouldOverflow =
      isOverflowingDocket(
        docketItems,
        itemIndex,
        columnHeight,
        simHeight,
        continueHeight,
        scale,
        true,
      ) ||
      (simHeight + heightNeeded > columnHeight && !isLastItemForDocket) ||
      simHeight + itemHeight > columnHeight ||
      (itemIndex > 0 &&
        headerOrder.some((headerType, priorityIndex) => {
          if (item.type === headerType) {
            if (priorityIndex === 0) return true;
            const previousItemType = docketItems[itemIndex - 1]?.type;
            return !headerOrder
              .slice(0, priorityIndex)
              .includes(previousItemType);
          }
          return false;
        }));

    // Use the simplified substantial content logic
    const needNewColumn = shouldBreakToNewColumn(
      wouldOverflow,
      item,
      itemIndex,
      currentColumnItems,
    );

    if (needNewColumn) {
      totalPositions++;
      simHeight = continuedHeight;
      currentColumnItems = [];
    }

    currentColumnItems.push(item);
    simHeight += itemHeight;
  }

  return totalPositions;
};

const addContinueTag = (
  pages: PaginatedDocketItems[],
  docketPage: number,
  docketColumn: number,
  docketRow: number,
  docketId: string,
  positionCount: number,
  totalPositions: number,
  docketStatus: DocketStatus,
) => {
  const currentColumnObj = pages[docketPage - 1].columns.find(
    c => c.column === docketColumn,
  );
  const currentRowObj = currentColumnObj?.rows.find(r => r.row === docketRow);

  if (currentRowObj) {
    currentRowObj.items.push({
      type: DocketDisplayItemType.CONTINUE,
      name: CONTINUE,
      height: DocketDisplayItemTypeHeight[DocketDisplayItemType.CONTINUE],
      docketId,
      status: docketStatus,
      position: {
        parentRowPosition: docketRow,
        pageIndicator: `${positionCount}/${totalPositions}`,
      },
    });
  }
};

const addContinuedTag = (
  pages: PaginatedDocketItems[],
  docketPage: number,
  docketColumn: number,
  docketRow: number,
  docketId: string,
  orderNumber: string,
  positionCount: number,
  totalPositions: number,
  docketStatus: DocketStatus,
) => {
  const newColumnObj = pages[docketPage - 1].columns.find(
    c => c.column === docketColumn,
  );
  const newRowObj = newColumnObj?.rows.find(r => r.row === docketRow);

  if (newRowObj) {
    newRowObj.items.push({
      type: DocketDisplayItemType.CONTINUED,
      name: CONTINUE,
      orderNumber: orderNumber,
      height: DocketDisplayItemTypeHeight[DocketDisplayItemType.CONTINUED],
      docketId,
      status: docketStatus,
      position: {
        parentRowPosition: docketRow,
        pageIndicator: `${positionCount}/${totalPositions}`,
      },
    });
  }
};

export const getGridPaginatedDocketItems = (
  items: DocketDisplayItem[],
  scale: (n: number) => number,
  columnHeight: number,
  colsPerPage: number,
  rowPerPage: number,
): PaginatedDocketItems[] => {
  const continueHeight = scale(
    DocketDisplayItemTypeHeight[DocketDisplayItemType.CONTINUE],
  );
  const continuedHeight = scale(
    DocketDisplayItemTypeHeight[DocketDisplayItemType.CONTINUED],
  );

  const filteredItems = items.filter(
    item =>
      item.type !== DocketDisplayItemType.CONTINUE &&
      item.type !== DocketDisplayItemType.CONTINUED,
  );

  const docketGroups: DocketDisplayItem[][] = [];
  let currentDocket: DocketDisplayItem[] = [];

  filteredItems.forEach(item => {
    const shouldStartNewDocket = headerOrder.includes(item.type);
    if (shouldStartNewDocket) {
      if (currentDocket.length > 0) {
        docketGroups.push(currentDocket);
      }
      currentDocket = [item];
    } else {
      currentDocket.push(item);
    }
  });

  if (currentDocket.length > 0) {
    docketGroups.push(currentDocket);
  }

  // Merge standalone header groups with subsequent groups if they're small
  const mergedDocketGroups: DocketDisplayItem[][] = [];
  for (let i = 0; i < docketGroups.length; i++) {
    const currentGroup = docketGroups[i];
    const nextGroup = docketGroups[i + 1];

    if (
      currentGroup.length === 1 &&
      headerOrder.includes(currentGroup[0].type) &&
      nextGroup
    ) {
      mergedDocketGroups.push([...currentGroup, ...nextGroup]);
      i++;
    } else {
      mergedDocketGroups.push(currentGroup);
    }
  }

  const finalDocketGroups = mergedDocketGroups;

  const pages: PaginatedDocketItems[] = [
    createPage(1, colsPerPage, rowPerPage),
  ];
  let currentPage = 1;
  let currentRow = 1;
  let currentColumn = 1;

  finalDocketGroups.forEach((docketItems, docketIndex) => {
    if (docketIndex > 0) {
      const isHeaderOnlyGroup =
        docketItems.length === 1 && headerOrder.includes(docketItems[0].type);

      if (!isHeaderOnlyGroup) {
        currentColumn++;

        if (currentColumn > colsPerPage) {
          currentColumn = 1;
          currentRow++;

          if (currentRow > rowPerPage) {
            currentRow = 1;
            currentPage++;

            if (pages.length < currentPage) {
              pages.push(createPage(currentPage, colsPerPage, rowPerPage));
            }
          }
        }
      }
    }

    if (checkForOrphans(docketItems, scale, columnHeight, continueHeight)) {
      let docketHeight = 0;
      let docketColumn = currentColumn;
      let docketRow = currentRow;
      let docketPage = currentPage;
      let positionCount = 1;
      let currentColumnItems: DocketDisplayItem[] = [];

      const totalPositions = calculateTotalPositions(
        docketItems,
        scale,
        columnHeight,
        continueHeight,
        continuedHeight,
      );

      const docketId = getDocketId(docketItems);
      const docketStatus = getDocketStatus(docketItems);
      const orderNumber = getOrderNumber(docketItems);

      docketItems.forEach((item, itemIndex) => {
        const itemHeight = scale(item.height);
        const isLastItemForDocket = itemIndex === docketItems.length - 1;
        const heightNeeded = isLastItemForDocket
          ? itemHeight
          : itemHeight + continueHeight;

        const wouldOverflow =
          isOverflowingDocket(
            docketItems,
            itemIndex,
            columnHeight,
            docketHeight,
            continueHeight,
            scale,
            true,
          ) ||
          (docketHeight + heightNeeded > columnHeight &&
            !isLastItemForDocket) ||
          docketHeight + itemHeight > columnHeight ||
          (itemIndex > 0 &&
            headerOrder.some((headerType, priorityIndex) => {
              if (item.type === headerType) {
                if (priorityIndex === 0) return true;
                const previousItemType = docketItems[itemIndex - 1]?.type;
                return !headerOrder
                  .slice(0, priorityIndex)
                  .includes(previousItemType);
              }
              return false;
            }));

        // Use the simplified substantial content logic
        const needNewColumn = shouldBreakToNewColumn(
          wouldOverflow,
          item,
          itemIndex,
          currentColumnItems,
        );

        if (needNewColumn) {
          const isHeaderItem = headerOrder.includes(item.type);

          if (!isHeaderItem && positionCount < totalPositions) {
            addContinueTag(
              pages,
              docketPage,
              docketColumn,
              docketRow,
              docketId,
              positionCount,
              totalPositions,
              docketStatus,
            );
          }

          docketColumn++;
          if (docketColumn > colsPerPage) {
            docketColumn = 1;
            docketRow++;
            if (docketRow > rowPerPage) {
              docketRow = 1;
              docketPage++;
              if (pages.length < docketPage) {
                pages.push(createPage(docketPage, colsPerPage, rowPerPage));
              }
            }
          }

          positionCount++;

          if (!isHeaderItem && positionCount > 1) {
            addContinuedTag(
              pages,
              docketPage,
              docketColumn,
              docketRow,
              docketId,
              orderNumber,
              positionCount,
              totalPositions,
              docketStatus,
            );
            docketHeight = continuedHeight;
          } else {
            docketHeight = 0;
          }

          // Reset current column items tracking
          currentColumnItems = [];
        }

        const targetColumn = pages[docketPage - 1].columns.find(
          c => c.column === docketColumn,
        );
        const targetRowObj = targetColumn?.rows.find(r => r.row === docketRow);

        if (targetRowObj) {
          targetRowObj.items.push(item);
          currentColumnItems.push(item);
        }

        docketHeight += itemHeight;
      });

      currentPage = docketPage;
      currentRow = docketRow;
      currentColumn = docketColumn;
    } else {
      const targetColumn = pages[currentPage - 1].columns.find(
        c => c.column === currentColumn,
      );
      const targetRowObj = targetColumn?.rows.find(r => r.row === currentRow);

      if (targetRowObj) {
        targetRowObj.items.push(...docketItems);
      }
    }
  });

  return pages;
};
